// SAL6010_R01 정보(헤더) 섹션
// - 기본 정보 입력/코드검색/주소검색/금액 자동계산/수주처리 버튼 제공
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {  searchCustomer } from "src/apis/customerApi";
import { AddressForm } from "src/components/common/form/address/AddressForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { AddressJusoDto } from "src/dtos/address/addressApiResponseDto";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import useUiuxStore from "src/store/useUiuxStore";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

import { blueButtonClass, BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { handleKeyDown, syntheticEvent } from "src/utils/commonForm/commonFormUtils";
import { defaultSAL6010_R01HeaderDto, SAL6010_R01HeaderDto } from "./Dto_SAL6010_R01";
import { useSAL6010_R01Store } from "./Store_SAL6010_R01";
import { getEmployeeList } from "src/apis/employeeApi";
import { formatDate } from "src/utils/string/stringUtil";



interface Props {
	editable: boolean
	defaultSAL6010_R01HeaderRow: SAL6010_R01HeaderDto;
	onOrderProcessClick?: () => void;
}

export const Info_SAL6010_R01 = (props: Props) => {

	const {
		editable, onOrderProcessClick,
		defaultSAL6010_R01HeaderRow
	} = props;

	// 
	const { vatTypeList, moneyCdList, compCdList } = useCommonCodeStore();
	const { setCodeSearchModalProps, setAddressModalProps } = useUiuxStore();
	const { SAL6010_R01DetailList, deletedSAL6010_R01DetailList,
			editSAL6010_R01, setEditSAL6010_R01, selectedSAL6010_R01,
	} = useSAL6010_R01Store();

	// 코드검색 포커스 타겟 아이디
	const custInfoHandleFoucsId = 'infoCustInput';
	const empInfoHandleFoucsId = 'infoEmpInput';
	const addressHandleFoucsId = 'addressInput2';

	// 코드검색 필드 설정 (거래처/담당자)
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: custInfoHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
				post: 'post',
				address: 'address',
				detailAddress: 'detailAddress',
				telNo: 'telNo',
				faxNo: 'faxNo',
				moneyCd: 'moneyCd'
			},
			searchFunction: async (searchText: string) => {
				const customerResponse = await searchCustomer(searchText);
				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: editSAL6010_R01?.customCd || '',
				customNm: editSAL6010_R01?.customNm || '',
			}),
		},
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: empInfoHandleFoucsId,
			responseMapping: {
				empNo: 'empNo',
				empNm: 'empNm'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			},
			extraFilter: () => ({
				empNo: editSAL6010_R01?.empNo || '',
				empNm: editSAL6010_R01?.empNm || '',
			}),
		}
		
	];

	// Info 편집용 공통 핸들러
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editSAL6010_R01,
		setEditData: (data) => {
			// empNo가 변경될 때 estimateEmpNo도 함께 설정
			if (data && 'empNo' in data && data.empNo !== editSAL6010_R01?.empNo) {
				setEditSAL6010_R01({
					...data,
					estimateEmpNo: data.empNo
				});
			} else {
				setEditSAL6010_R01(data);
			}
		},
		defaultData: defaultSAL6010_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'SAL6010_R01'
	});

		// 출고자 입력 피커 상태
	const [deliveryPickerVisible, setDeliveryPickerVisible] = useState(false);
	const [pickerPos, setPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 매출자 입력 피커 상태
	const [salesPickerVisible, setSalesPickerVisible] = useState(false);
	const [salesPickerPos, setSalesPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 부가세 입력 포커스 상태
	const [vatAmtInputFocused, setVatAmtInputFocused] = useState(false);
	const [vatAmtInputValue, setVatAmtInputValue] = useState('');
	const [vatAmtManuallyEdited, setVatAmtManuallyEdited] = useState(false);

	// 금액 계산 메모이제이션 (부가세 자동계산 포함)
	const calculatedAmounts = useMemo(() => {
		const newSalAmt = SAL6010_R01DetailList.reduce((sum, item) => 
			sum + (deletedSAL6010_R01DetailList.includes(String(item.valueIndex ?? '')) ? 0 : item.itemAmt), 0);
		
		// 조회 시에는 DB 그룹용, 입력 시에는 동기 계산 용
		let newVatAmt = editSAL6010_R01?.vatAmt || 0;
		
		// 부가구분 반과세인 경우 (코드명에 '반'은 '과세'가 포함된 경우)
		const currentVatType = vatTypeList.find(vat => vat.minorCd === editSAL6010_R01?.vatType);
		const isGeneralTax = currentVatType?.codeNm?.includes('반') || currentVatType?.codeNm?.includes('과세') || editSAL6010_R01?.vatType === '10';
		
		if (isGeneralTax && !vatAmtManuallyEdited && SAL6010_R01DetailList.some(item => item.inserted || item.isModified)) {
			newVatAmt = Math.round(newSalAmt * 0.1); // 매출의 10% (반올림)
		} else if (vatAmtManuallyEdited) {
			newVatAmt = editSAL6010_R01?.vatAmt || 0;
		} else {
			newVatAmt = editSAL6010_R01?.vatAmt || 0;
		}
		
		// 공급총액 확인 계산
		const newTotalAmt = newSalAmt + newVatAmt;
		
		return { newSalAmt, newVatAmt, newTotalAmt };
	}, [SAL6010_R01DetailList, deletedSAL6010_R01DetailList, editSAL6010_R01?.vatAmt, editSAL6010_R01?.vatType, vatTypeList, vatAmtManuallyEdited]);

	useEffect(() => {
		if (calculatedAmounts) {
			setEditSAL6010_R01({
				...defaultSAL6010_R01HeaderRow,
				...editSAL6010_R01,
				salAmt: calculatedAmounts.newSalAmt,
				vatAmt: calculatedAmounts.newVatAmt,
				totalAmt: calculatedAmounts.newTotalAmt
			});
		}
	}, [calculatedAmounts]);

	useEffect(() => {
		// editSAL6010_R01.vatAmt가 바뀌면 실시간도 갱신
		if (!vatAmtInputFocused) {
			setVatAmtInputValue(Number(editSAL6010_R01?.vatAmt || 0).toLocaleString());
		}
	}, [editSAL6010_R01?.vatAmt, vatAmtInputFocused]);

	useEffect(() => {
		if (editSAL6010_R01?.salBillNo && !SAL6010_R01DetailList.some(item => item.inserted || item.isModified)) {
			setVatAmtManuallyEdited(false);
		}

	}, [editSAL6010_R01?.salBillNo, SAL6010_R01DetailList]);

	const handleDateChange = useCallback((field: keyof SAL6010_R01HeaderDto, value: any) => {
		setEditSAL6010_R01({
			...defaultSAL6010_R01HeaderRow,
			...editSAL6010_R01,
			[field]: value
		});
	}, [editSAL6010_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'salAmt' || name === 'vatAmt') {
			const salAmt = name === 'salAmt' ? Number(value) : Number(editSAL6010_R01?.salAmt ?? 0);
			const vatAmt = name === 'vatAmt' ? Number(value) : Number(editSAL6010_R01?.vatAmt ?? 0);
			
			// 부가세를 수동으로 수정한 경우
			if (name === 'vatAmt') {
				setVatAmtManuallyEdited(true); // 수동 편집 상태로 설정
			}
			setEditSAL6010_R01({
				...defaultSAL6010_R01HeaderRow,
				...editSAL6010_R01,
				[name]: Number(value), // 숫자로 변환하여 저장
				totalAmt: salAmt + vatAmt
			});
			return;
		}

		// 부가세 구분이 변경될 때 자동 계산
		if (name === 'vatType') {
			let newVatAmt = editSAL6010_R01?.vatAmt || 0;
			
			// 부가세 구분이 일반과세인지 확인
			const selectedVatType = vatTypeList.find(vat => vat.minorCd === value);
			const isGeneralTax = selectedVatType?.codeNm?.includes('일반') || selectedVatType?.codeNm?.includes('과세') || value === '10';
			
			// 부가세 구분이 변경되면 수동 편집 상태를 리셋하고 자동 계산
			if (isGeneralTax) {
				newVatAmt = Number(editSAL6010_R01?.salAmt || 0) * 0.1; // 반올림 없음
				setVatAmtManuallyEdited(false); // 부가세 구분 변경 시 수동 편집 상태 리셋
			}
			
			setEditSAL6010_R01({
				...defaultSAL6010_R01HeaderRow,
				...editSAL6010_R01,
				[name]: value,
				vatAmt: newVatAmt,
				totalAmt: Number(editSAL6010_R01?.salAmt || 0) + newVatAmt
			});
			return;
		}

		setEditSAL6010_R01({
			...defaultSAL6010_R01HeaderRow,
			...editSAL6010_R01,
			[name]: value
		});
	}, [editSAL6010_R01]);

	const handleAddressSearchClick = () => {
		setAddressModalProps({
			onSelect: (data: AddressJusoDto) => {
				setEditSAL6010_R01({
					...defaultSAL6010_R01HeaderRow,
					...editSAL6010_R01,
					post: data.zipNo,
					address: data.roadAddr,
				})
				setAddressModalProps(undefined);
				handleKeyDown(syntheticEvent(addressHandleFoucsId), 'focus');
			},
			handleFoucsId: addressHandleFoucsId // 주소 검색 후 입력 포커스 이동을 위한 아이디
		})
	}

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300">
				{/* editSAL6010_R01 : {JSON.stringify(editSAL6010_R01)} <br /> */}
				<InputForm title="견적번호" value={editSAL6010_R01?.salesEstimateNo || ""} 
					editable={false}
					name="salesEstimateNo" 
					onChange={handleChange} />

					<BRDiv />

				<SingleDateForm
					title="견적일자"
					value={editSAL6010_R01?.estimateDt ? new Date(editSAL6010_R01.estimateDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('estimateDt', formatDate(value))}
					editable={true}
					visible={salesPickerVisible}				setVisible={setSalesPickerVisible}
					onChange={handleChange}
					pickerPos={salesPickerPos}						setPickerPos={setSalesPickerPos}
				/>
				
					<BRDiv />
				<CodeForm
					label="거래처"
					value={editSAL6010_R01?.customCd || ""}
					custNmValue={editSAL6010_R01?.customNm || ""}
					editable={true}
					handleFoucsId={custInfoHandleFoucsId}
					name="customCd"
					handleChange={getFieldHandlers('customCd').onChange}
					onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
					onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
				/>
				<DropDownForm
						title="부가세구분" 
						value={editSAL6010_R01?.vatType}
						name="vatType"
						initLabelLess={true}
						data={vatTypeList}
						editable={true}
						onChange={handleChange} />
					<BRDiv />
				
				<InputForm		title="연락처" 
						value={editSAL6010_R01?.telNo || ""} 
						editable={false} 
						name="telNo" 
						onChange={handleChange}
					/>
				<InputForm		title="팩스" 
					value={editSAL6010_R01?.faxNo || ""} 
					editable={false} 
					name="faxNo" 
					onChange={handleChange}
				/>
				<BRDiv />
				<InputForm title = "이메일" value={editSAL6010_R01?.email || ""} 
					editable={true} 
					name="email" 
					onChange={handleChange}
				/>
				<BRDiv />
				<AddressForm
					post={editSAL6010_R01?.post || ""}
					address={editSAL6010_R01?.address || ""}
					detailAddress={editSAL6010_R01?.detailAddress || ""}
					editable={true}
					handleFoucsId={addressHandleFoucsId}
					onChange={handleChange}
					onSearchClick={handleAddressSearchClick}
				/>
				
					<BRDiv />

					<CodeForm
					label="견적담당"
					value={editSAL6010_R01?.empNo || ""}
					custNmValue={editSAL6010_R01?.empNm || ""}
					editable={true}
					name="empNo"
					handleChange={getFieldHandlers('empNo').onChange}
					onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
					onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
				/>
				<BRDiv />
				<InputForm	title="견적금액" 
							value={editSAL6010_R01?.salAmt || 0} 
							editable={false} 
							name="salAmt" 
							onChange={handleChange} 
							/>

				<SingleDateForm
					title="견적 유효기간"
					value={editSAL6010_R01?.dueDt ? new Date(editSAL6010_R01.dueDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('dueDt', formatDate(value))}
					editable={true}
					visible={deliveryPickerVisible}				setVisible={setDeliveryPickerVisible}
					onChange={handleChange}
					pickerPos={pickerPos}						setPickerPos={setPickerPos}
				/>
				<BRDiv /> {/* 추가 필요 */}
				
				<InputForm title="비고" value={editSAL6010_R01?.remark || ""} editable={true} wrapperOption={{
				wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[10px]",
				titleStyle: "w-[100px] min-w-[100px] text-left",
				inputStyle: "w-full min-w-full"
			}} name="remark" onChange={handleChange} />
				
				<BRDiv />
				
				{/* 수주 처리 버튼 */}
				<div className="flex items-end">
					<button
						onClick={onOrderProcessClick}
						className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
						disabled={!selectedSAL6010_R01}
						title={!selectedSAL6010_R01 ? "수주 처리할 견적서를 선택해주세요" : "선택된 견적서를 수주로 전환"}
					>
						수주 처리
					</button>
				</div>

		</div>
	)
}
